import React, { useState, useEffect, useRef } from "react";

interface DropdownOption {
  code: string;
  name: string;
}

interface SearchableDropdownProps {
  options: DropdownOption[];
  label: string;
  id: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
}

const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
  options,
  label,
  id,
  value,
  onChange,
  required = true,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [filteredOptions, setFilteredOptions] =
    useState<DropdownOption[]>(options);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Filter options when search term or options change
  useEffect(() => {
    if (searchTerm) {
      const filtered = options.filter((option) =>
        option.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredOptions(filtered);
    } else {
      setFilteredOptions(options);
    }
  }, [searchTerm, options]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Focus input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 10);
    }
  }, [isOpen]);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setSearchTerm("");
      setFilteredOptions(options);
    }
  };

  const handleSelect = (code: string) => {
    onChange(code);
    setIsOpen(false);
    setSearchTerm("");
  };

  // Searchability
  const selectedName =
    options.find((option) => option.code === value)?.name || "";

  return (
    <div
      className="select-container"
      style={{
        marginBottom: "1.5rem",
        fontFamily: "'Arial', sans-serif",
      }}
    >
      <label
        htmlFor={id}
        style={{
          display: "block",
          marginBottom: "0.5rem",
          fontSize: "1.2rem",
          fontWeight: "bold",
        }}
      >
        {label}
        {required && (
          <span style={{ color: "red", marginLeft: "0.2rem" }}>*</span>
        )}
      </label>

      <div
        className="dropdown"
        ref={dropdownRef}
        style={{
          position: "relative",
          width: "100%",
          maxWidth: "400px",
        }}
      >
        <button
          className="form-select"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "0.8rem",
            fontSize: "1rem",
            width: "100%",
            border: "1px solid #ccc",
            borderRadius: "8px",
            cursor: "pointer",
            background: "#fff",
            boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
          }}
          type="button"
          id={`dropdown-${id}`}
          onClick={toggleDropdown}
          aria-expanded={isOpen}
        >
          <span>{value ? selectedName : `Select ${label}`}</span>
          <i className={`fas fa-chevron-${isOpen ? "up" : "down"}`} />
        </button>

        {isOpen && (
          <div
            className="dropdown-menu"
            style={{
              position: "absolute",
              top: "100%",
              left: 0,
              width: "100%",
              background: "#fff",
              border: "1px solid #ccc",
              borderRadius: "8px",
              boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
              marginTop: "0.5rem",
              zIndex: 10,
              maxHeight: "300px",
              overflowY: "auto",
            }}
          >
            <div style={{ padding: "0.5rem" }}>
              <input
                ref={inputRef}
                type="text"
                style={{
                  width: "100%",
                  padding: "0.5rem",
                  borderRadius: "4px",
                  border: "1px solid #ccc",
                }}
                placeholder={`Search ${label}...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
            <div style={{ borderTop: "1px solid #eee" }} />
            <div>
              {filteredOptions.length === 0 ? (
                <div
                  style={{
                    padding: "0.8rem",
                    color: "#999",
                    textAlign: "center",
                  }}
                >
                  No results found
                </div>
              ) : (
                filteredOptions.map((option) => (
                  <button
                    key={option.code}
                    style={{
                      display: "block",
                      width: "100%",
                      padding: "0.8rem",
                      textAlign: "left",
                      background: value === option.code ? "#E6F7FF" : "#fff",
                      border: "none",
                      cursor: "pointer",
                    }}
                    onClick={() => handleSelect(option.code)}
                  >
                    {option.name}
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      <input
        type="hidden"
        id={id}
        name={id}
        value={value}
        required={required}
      />
    </div>
  );
};

export default SearchableDropdown;
